import React, { Fragment, useEffect, useState, ReactNode, memo, useMemo, useRef } from 'react'
import useOnBoardSchedule from '../../../../hooks/useOnBoardSchedule'
import { ButtonGroup, Dropdown } from 'react-bootstrap'
import {
    GroundTaskSvg,
    PayloadTaskSvg,
    SubSystemSvg,
    TaskUpcommingSvg,
} from '../../modals/Svgs'
import './Schedule.scss'
import useUrlNavigation from '../../../../hooks/useUrlNavigation'
import useUrlParams from '../../../../hooks/useUrlParams'
import PlanningNavigation from '../PlanningNavigation'
import { get_navigator_button_by_dashboard } from '../utils'
import useContacts from '../../../../hooks/useContacts'
import useViewer from '../../../../hooks/useViewer'
import { get_formated_time, get_task_status_icon } from '../../../../utils'
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip'
import EmptyPage from '../../../../custom_components/EmptyPage'
import UploadScheduleButton from '../../../../custom_components/UploadScheduleButton'
import GenerateScheduleBurtton from '../../../../custom_components/GenerateScheduleBurtton'
import { replaceUnderscoresWithSpaces } from '../../../../../../utils/commonFunctions'
import { ScheduleTask } from '../../../../types'
import ConfirmationDialog from '../../../../../CommonComponents/confirmation_dialog/ConfirmationDialog'
import _ from "lodash";
import { NotSchedulesvg } from '../../../../../simulate/scenario/svgs/Svgs'
import useScenario from '../../../../hooks/useScenario';
import { isOperateMode, isSummaryMode } from '../../../../hooks/utils';
import LeftSidebar from './custom-schedule/LeftSidebar'
import RightSection from './custom-schedule/RightSection'
import { NoSchedulePreview } from '../Svgs'
import useCustomSchedule from '../../../../hooks/useCustomSchedule'
import { isValidationSchedule } from './custom-schedule/scheduleRowUtils'

/**
 * CT - compact tooltip wrapper
 * Moved OUTSIDE the main component and memoized to prevent it being recreated on every render.
 */
type CTProps = {
    title: string;
    children: ReactNode;
    placement?: string;
}
const CT = memo(({ title, children, placement = 'top-start' }: CTProps) => {
    return (
        <CustomToolTip title={title} placement={placement as any}>
            {children}
        </CustomToolTip>
    )
});
CT.displayName = 'CT';

/**
 * OnboardSchedule component
 */
const OnboardSchedule = () => {
    const { tab_name, dashboard, scenario_id, mission_id } = useUrlParams()
    const { scenario } = useScenario()
    const operateMode = isOperateMode(dashboard, scenario)
    const summaryMode = isSummaryMode(dashboard, scenario)
    const isValidateSummary = (scenario?.scenarioDeploymentType ?? '').toUpperCase() === 'VALIDATE' && summaryMode
    const { selectedScheduleTask, allOnBoardSchedule, select_schedule, selectedSchedule, setSelectedScheduleTask, get_all_onboard_schedules } = useContacts()

    const { navigate_to } = useUrlNavigation()
    const { current_simulation_time } = useViewer()
    const { onboardScheduleDetails, onMenuClick, onTaskSelect, onCommandSelect } = useCustomSchedule()
    const { scheduleDetails, scheduleData, select_schedule_data, exportOnBoardOperateSchedule, deleteOnBoardOperateSchedule } = useOnBoardSchedule()
    const [isDeleteScheduleId, setIsDeleteScheduleId] = useState<string>('');
    const [expandedCommandId, setExpandedCommandId] = useState<string | null>(null);

    const hasFetchedOnMountRef = useRef(false);

    // Fetch onboard schedules ONCE on entering/mounting the plan view
    useEffect(() => {
        if (!hasFetchedOnMountRef.current) {
            hasFetchedOnMountRef.current = true;
            get_all_onboard_schedules();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // State for automatic selections
    const [autoSelectedScheduleId, setAutoSelectedScheduleId] = useState<string | null>(null);
    const [autoSelectedTaskId, setAutoSelectedTaskId] = useState<string | null>(null);
    const [middleTaskSelector, setMiddleTaskSelector] = useState(0)

    const deploymentType = useMemo(() => scenario?.scenarioDeploymentType, [scenario?.scenarioDeploymentType])

    // Auto-select first schedule when data loads (operate dashboard)
    useEffect(() => {
        if (operateMode && allOnBoardSchedule && Object.keys(allOnBoardSchedule).length > 0) {
            // Find first non-empty schedule group
            const firstGroup = Object.keys(allOnBoardSchedule).find(
                key => allOnBoardSchedule[key].length > 0
            );

            if (firstGroup) {
                const firstSchedule = allOnBoardSchedule[firstGroup][0];
                if (firstSchedule?.ScheduleId && firstSchedule.ScheduleId !== selectedSchedule) {
                    setAutoSelectedScheduleId(firstSchedule.ScheduleId);
                    select_schedule(firstSchedule);
                }
            }
        }
    }, [allOnBoardSchedule, dashboard, operateMode]);

    // Auto-select first task when schedule changes
    useEffect(() => {
        if (scheduleData?.length > 0) {
            const firstTask = scheduleData[0];
            if (firstTask && firstTask.taskId !== selectedScheduleTask?.schedule_data?.taskId) {
                setAutoSelectedTaskId(firstTask.taskId);
                select_schedule_data(firstTask);

                // Auto-expand first command when available
                if (firstTask?.commandsList?.length > 0) {
                    setExpandedCommandId(firstTask.commandsList[0]?.['commandId']);
                }
            }
        } else {
            setAutoSelectedTaskId(null);
        }
    }, [scheduleData]);

    // Auto-expand first command when selectedScheduleTask changes (if none expanded)
    useEffect(() => {
        if (selectedScheduleTask?.schedule_data?.commandsList?.length > 0 && !expandedCommandId) {
            setExpandedCommandId(selectedScheduleTask.schedule_data.commandsList[0]?.['commandId']);
        }
    }, [selectedScheduleTask]);

    // Toggle which command accordion is expanded
    const handleToggle = (id: string) => {
        setExpandedCommandId(prevId => (prevId === id ? null : id));
    };

    const formatStatusLabel = (rawStatus: string) => {
        const s = replaceUnderscoresWithSpaces(rawStatus).toLowerCase().trim();
        if (!s) return 'NA';
        return s.charAt(0).toUpperCase() + s.slice(1);
    };

    // Format status from backend: user-friendly label + stable css class
    const get_schedule_status = (status: string) => {
        const raw = status?.trim();
        if (!raw) return { label: 'NA', status: 'na' };

        return {
            label: formatStatusLabel(raw),
            status: raw.toLowerCase(),
        };
    };

    return (
        <Fragment>
            {!operateMode &&
                <div className='planning__container__body'>
                    {/* Header with navigator buttons */}
                    <div className='planning__container__header'>
                         <div className='planning__header__left__container'>
                            {get_navigator_button_by_dashboard(deploymentType!)
                                .filter((btn) => !isValidateSummary || btn.name !== 'plan-ground-contact')
                                .map((btn) => {
                                return (
                                    <button
                                        key={btn.name}
                                        className={`button ${tab_name === btn.name && 'active'}`}
                                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                            e.stopPropagation()
                                            navigate_to(btn.name)
                                        }}
                                    >{btn.label}</button>
                                )
                            })}
                        </div>
                    </div>

                    {/* Main schedule container (only render when scheduleData exists) */}
                    {(scheduleData && scheduleData?.length > 0) &&
                        <div className='schedule__container'>
                            <Fragment>

                                {/* LEFT: schedule list (only for operate dashboard) */}
                                {operateMode &&
                                    <Fragment>
                                        <div className='left__schedule__panel__container'>
                                            <div className='left__schedule__list__panel__container__header'>
                                                <span>List of Schedules</span>
                                                {/* Needed in future */}
                                                {/* <div className='button__section'>
                                            <button><SearchSvg color='#F0F0F0' /></button>
                                            <button><FilterSvg /></button>
                                        </div> */}
                                            </div>

                                            <div className='right__schedule__panel__container__table__body gap-3'>
                                                {Object.keys(allOnBoardSchedule).map(key => {
                                                    const all_schedules = allOnBoardSchedule?.[key]

                                                    return (
                                                        <div className='d-flex flex-column gap-2' key={key}>
                                                            <span className='opacity-75'>{key}</span>

                                                            {/* Map each schedule in this group */}
                                                            {all_schedules?.map(schedule => {
                                                                const status = get_schedule_status(schedule?.['status'])

                                                                // isSelected if either user selected or auto selected
                                                                const isSelected = selectedSchedule === schedule?.['ScheduleId'] ||
                                                                    autoSelectedScheduleId === schedule?.['ScheduleId']


                                                                const scheduleTooltipTitle = schedule?.['ScheduleName'] || 'Untitled Schedule'

                                                                return (
                                                                    <div
                                                                        key={schedule?.['ScheduleId']}
                                                                        className={`schedule__details__container ${key === 'Ongoing' && 'running'} ${isSelected && 'active'}`}
                                                                        onClick={() => {
                                                                            setMiddleTaskSelector(0)
                                                                            setAutoSelectedScheduleId(null);
                                                                            setAutoSelectedTaskId(null);        // reset auto-selected task
                                                                            setExpandedCommandId(null);         // reset expanded command accordion
                                                                            select_schedule(schedule);
                                                                            select_schedule_data(schedule);
                                                                        }}
                                                                    >
                                                                        <div className='details__container'>

                                                                            {/* Top row: name (with tooltip) and action button(s) */}
                                                                            <div className='d-flex align-items-center justify-content-between w-100'>
                                                                                <span className={`details__container__name ${status.status}`}>
                                                                                    {/* Reused tooltipTitle via memoized CT wrapper */}
                                                                                    <CT title={scheduleTooltipTitle}>
                                                                                        {scheduleTooltipTitle}
                                                                                    </CT>
                                                                                </span>

                                                                            </div>

                                                                            {/* Bottom row: Start time and schedule status */}
                                                                            <div className='d-flex align-items-center justify-content-between w-100'>
                                                                                <span className={`details__container__time`}>{schedule?.['StartTime'] || '--'}</span>
                                                                                <span className={`details__container__status ${status.status}`}>{status.label}</span>
                                                                            </div>
                                                                        </div>

                                                                        {/* Right side dropdown for export/delete (operate only) */}
                                                                        {operateMode && <div className='flex-1 d-flex justify-content-end button__container m-auto'>
                                                                            <ButtonGroup size="sm" className="flex-nowrap mb-0">
                                                                                <div className="btn-list more-button">
                                                                                    <Dropdown drop="end">
                                                                                        <Dropdown.Toggle
                                                                                            size="sm"
                                                                                            variant="transparent"
                                                                                            className='pr-0'
                                                                                        >
                                                                                            <i className="fe fe-more-vertical fs-20"></i>
                                                                                        </Dropdown.Toggle>
                                                                                        <Dropdown.Menu className='button__dropdown__container'>
                                                                                            {!isValidationSchedule(schedule, scenario) && (
                                                                                                <Dropdown.Item
                                                                                                    className='button__dropdown__item'
                                                                                                    onClick={(e) => {
                                                                                                        exportOnBoardOperateSchedule(mission_id, scenario_id, schedule?.ScheduleId || schedule?.scheduleId)
                                                                                                    }}
                                                                                                    eventKey="0"
                                                                                                >
                                                                                                    Export
                                                                                                </Dropdown.Item>
                                                                                            )}
                                                                                            <Dropdown.Item
                                                                                                className='button__dropdown__item'
                                                                                                onClick={(e) => {
                                                                                                    setIsDeleteScheduleId(schedule?.ScheduleId || schedule?.scheduleId)
                                                                                                }}
                                                                                                eventKey="2"
                                                                                            >
                                                                                                Delete
                                                                                            </Dropdown.Item>
                                                                                        </Dropdown.Menu>
                                                                                    </Dropdown>
                                                                                </div>
                                                                            </ButtonGroup>
                                                                        </div>}
                                                                    </div>
                                                                )
                                                            })}
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                        <div className='schedule__panel__separator' />
                                    </Fragment>
                                }

                                {/**
                         * MIDDLE: tasks list for selected schedule
                         */}
                                <div
                                    className='left__schedule__panel__container'
                                    key={selectedSchedule || autoSelectedScheduleId || 'middle-panel'}
                                >
                                    <div className='left__schedule__panel__container__header'>
                                        <span className='header__title'>

                                            {
                                                !_.isUndefined(scheduleDetails?.name)
                                                    ? (() => {
                                                        const scheduleDetailsName = scheduleDetails.name
                                                        return (
                                                            <CT title={scheduleDetailsName}>
                                                                {scheduleDetailsName}
                                                            </CT>
                                                        )
                                                    })()
                                                    : '--'
                                            }
                                        </span>

                                        <div className='header__time__details'>
                                            <span>
                                                {!_.isUndefined(scheduleDetails?.start_time)
                                                    ? get_formated_time(scheduleDetails.start_time)
                                                    : '--'
                                                }
                                            </span>
                                        </div>

                                    </div>

                                    <div className='right__schedule__panel__container__table__header'>
                                        <span>Tasks</span>
                                        <span>{scheduleData?.length}</span>
                                    </div>

                                    <div className='right__schedule__panel__container__table__body'>
                                        {scheduleData?.length > 0 && scheduleData.map((task: ScheduleTask, index) => {
                                            const current_time = current_simulation_time / 1000
                                            const task_start_time = task.startTime;
                                            const task_end_time = task.endTime;
                                            const task_type_icon = task.type === 'Payload Task' ? <PayloadTaskSvg /> : <GroundTaskSvg />
                                            const task_status_icon = !operateMode ? get_task_status_icon(task_start_time, task_end_time, current_time) : <TaskUpcommingSvg />
                                            const sub_name = task.type === 'Payload Task' ? `${task?.['properties']?.['payloadName']} | ${task?.['properties']?.['sequenceName']}` : `${task?.['properties']?.['groundStationName']}`
                                            const task_running = (!operateMode && current_time >= task_start_time && current_time <= task_end_time) ? 'running' : ''

                                            const isSelected = index === middleTaskSelector


                                            const taskTitleTooltip = task?.['taskName'] // may be undefined — preserved original guard check below
                                            const taskSubNameTooltip = task.type === 'Payload Task'
                                                ? [
                                                    task?.['properties']?.['payloadName'],
                                                    task?.['properties']?.['sequenceName']
                                                ].filter(Boolean).join(' | ')
                                                : task?.['properties']?.['groundStationName'] || '--'

                                            const task_not_schedule = task?.status === "Not Possible"
                                            return (
                                                <Fragment key={task.taskId}>
                                                    <div className={`schedule__details__container ${task_running} ${isSelected && 'active'}`}
                                                        onClick={() => {
                                                            // When clicking a task, clear autoSelectedTask marker so manual interaction takes precedence
                                                            setAutoSelectedTaskId(null);
                                                            select_schedule_data(task);
                                                            setMiddleTaskSelector(index)
                                                        }}
                                                    >

                                                        <div className='schedule__icons'>
                                                            {!task_not_schedule && task_status_icon}
                                                            {!task_not_schedule && task_type_icon}
                                                            {task_not_schedule && (
                                                                <CT title="Task not scheduled">
                                                                    <span>
                                                                        <NotSchedulesvg />
                                                                    </span>
                                                                </CT>
                                                            )}
                                                        </div>



                                                        <div className='details__container'>
                                                            <span className='details__container__name'>
                                                                {
                                                                    !_.isUndefined(taskTitleTooltip)
                                                                        ? (
                                                                            // Reuse computed tooltip string for both title prop & inner content
                                                                            <CT title={taskTitleTooltip}>
                                                                                {taskTitleTooltip}
                                                                            </CT>
                                                                        )
                                                                        : '--'
                                                                }
                                                            </span>

                                                            <span className='details__container__sub_name'>
                                                                {/* Sub name tooltip always present in original code — reuse computed constant */}
                                                                <CT
                                                                    title={taskSubNameTooltip}
                                                                >
                                                                    {taskSubNameTooltip}
                                                                </CT>
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className='schedule__details__container__separator' />
                                                </Fragment>
                                            )
                                        })}
                                    </div>
                                </div>

                                {/* RIGHT: details & telecommands for selected task */}
                                <div className='schedule__panel__separator' />
                                <div className='right__schedule__panel__container'>

                                    <div className='right__schedule__panel__container__header'>
                                        <div className='header__title__details__container'>
                                            <div className='header__title__details'>
                                                <div className='d-flex gap-1'>
                                                    {selectedScheduleTask?.schedule_data?.type === 'Payload Task' ? <PayloadTaskSvg /> : <GroundTaskSvg />}

                                                    <span className='header__title'>
                                                        {
                                                            !_.isUndefined(selectedScheduleTask?.schedule_data?.['taskName'])
                                                                ? (() => {
                                                                    const selectedTaskName = selectedScheduleTask!.schedule_data!['taskName']
                                                                    return (
                                                                        <CT title={selectedTaskName}>
                                                                            {selectedTaskName}
                                                                        </CT>
                                                                    )
                                                                })()
                                                                : '--'
                                                        }
                                                    </span>
                                                </div>

                                                <span className='header__subtitle'>
                                                    {(() => {
                                                        const selectedTaskSubName = selectedScheduleTask?.schedule_data?.type === 'Payload Task'
                                                            ? [
                                                                selectedScheduleTask?.schedule_data?.['properties']?.['payloadName'],
                                                                selectedScheduleTask?.schedule_data?.['properties']?.['sequenceName']
                                                            ].filter(Boolean).join(' | ')
                                                            : selectedScheduleTask?.schedule_data?.['properties']?.['groundStationName'] || '--'

                                                        return (
                                                            <CT
                                                                title={selectedTaskSubName}
                                                            >
                                                                {selectedTaskSubName}
                                                            </CT>
                                                        )
                                                    })()}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Start - End time badges */}
                                        <div className='d-flex gap-3 align-items-center'>
                                            <div className='header_badge'>
                                                <span>
                                                    {!_.isUndefined(selectedScheduleTask?.schedule_data?.['startTime'])
                                                        ? get_formated_time(selectedScheduleTask?.schedule_data?.['startTime'])
                                                        : '--'}
                                                </span>
                                            </div>
                                            <span>to</span>
                                            <div className='header_badge'>
                                                <span>
                                                    {!_.isUndefined(selectedScheduleTask?.schedule_data?.['endTime'])
                                                        ? get_formated_time(selectedScheduleTask?.schedule_data?.['endTime'])
                                                        : '--'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='right__schedule__panel__container__table__header'>
                                        <span>Telecommands</span>
                                        <span>{selectedScheduleTask?.schedule_data?.['commandsList']?.length || 0}</span>
                                    </div>

                                    <div className='right__schedule__panel__container__table__body'>
                                        {selectedScheduleTask?.schedule_data &&
                                            <Fragment >
                                                <div className={`telecommands__details__container `}>
                                                    <div className='telecommands__details__header'>
                                                        <div className='telecommands__details__header__item'> SUBSYSTEM & COMMAND</div>
                                                        <div className='telecommands__details__header__item'>START TIME</div>
                                                        <div className='telecommands__details__header__item'>DURATION</div>
                                                    </div>

                                                    <div className='telecommands__details__body__container'>
                                                        {selectedScheduleTask?.schedule_data?.['commandsList']?.map((command, index) => {
                                                            const current_time = current_simulation_time / 1000;
                                                            const sub_task_start_time = command.epoch;
                                                            const sub_task_end_time = command.epoch + command.duration;
                                                            const sub_task_status_icon = !operateMode
                                                                ? get_task_status_icon(sub_task_start_time, sub_task_end_time, current_time)
                                                                : <TaskUpcommingSvg />;
                                                            const sub_task_running = (!operateMode &&
                                                                current_time >= sub_task_start_time &&
                                                                current_time <= sub_task_end_time)
                                                                ? 'running'
                                                                : '';

                                                            const isExpanded = expandedCommandId === command?.['commandId'];
                                                            const uniqueAccordionClass = `accordion__content_onboard_${index}`;

                                                            // Tokenize payload sequence and filter empty tokens (original logic)
                                                            const rawPayload = command?.['sequence'] || '';
                                                            const tokens = rawPayload
                                                                .split(/\s*,\s*/)
                                                                .filter(Boolean);

                                                            const filteredTokens = _.filter(tokens, (t) => !_.isNil(t) && _.trim(t) !== "");

                                                            return (
                                                                <Fragment key={command?.['commandId']}>
                                                                    <div
                                                                        className={`telecommands__details__body cursor-pointer ${sub_task_running}`}
                                                                        onClick={() => handleToggle(command?.['commandId'])}
                                                                    >
                                                                        <div className='telecommands__details__body__item'>
                                                                            <div className='schedule__icons'>{sub_task_status_icon}</div>
                                                                            <div className='details__container'>
                                                                                <div className='command__title__container'>
                                                                                    <SubSystemSvg />
                                                                                    <span>{command.name}</span>
                                                                                </div>
                                                                                <span>{command.subsystem}</span>
                                                                            </div>
                                                                        </div>
                                                                        <div className='telecommands__details__body__item'>
                                                                            <span>{get_formated_time(command.epoch)}</span>
                                                                        </div>
                                                                        <div className='telecommands__details__body__item'>
                                                                            <span>{command.duration} sec</span>
                                                                        </div>
                                                                    </div>

                                                                    {/* Accordion area for tokenized command sequence */}
                                                                    <div className={`
                                                                            ${uniqueAccordionClass} 
                                                                            accordion__content_onboard 
                                                                            ${isExpanded ? 'expanded' : ''} 
                                                                            ${_.isEmpty(filteredTokens) ? 'd-none' : ''}
                                                                            `}>
                                                                        <div className="accordion__inner_onboard">
                                                                            <div className="token-list">
                                                                                {filteredTokens.map((t, i) => (
                                                                                    <span className="token" key={i}>
                                                                                        &quot;{t}&quot;{i < filteredTokens.length - 1 ? ',' : ''}
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Fragment>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </Fragment>
                                        }
                                    </div>
                                </div>
                            </Fragment>
                        </div>
                    }

                    {/* Empty state */}
                    {(!scheduleData || scheduleData?.length === 0) && <EmptyPage content='Task Not Scheduled' classNames='w-100 h-100 botoom-0' />}

                    {/* Confirmation dialog for delete action */}
                    <ConfirmationDialog
                        type='delete'
                        open={isDeleteScheduleId ? true : false}
                        title={`Delete Schedule ?`}
                        description={"Are you sure you want to delete these Schedule ?"}
                        onCancel={() => setIsDeleteScheduleId('')}
                        onConfirm={() => {
                            deleteOnBoardOperateSchedule(mission_id, scenario_id, isDeleteScheduleId)
                            setIsDeleteScheduleId('');
                            setSelectedScheduleTask({
                                default_schedule: true,
                                schedule_data: {} as ScheduleTask
                            });
                        }}
                        confirm_button_text={`Delete`}
                    />
                </div>
            }
            {operateMode &&
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
                    {deploymentType && (
                        <div className='planning__container__header'>
                            <PlanningNavigation deploymentType={deploymentType} activeTab={tab_name} onNavigate={navigate_to} />
                            {(!summaryMode && !operateMode) && (
                                <div className='operate__planning__header__right__container'>
                                    <UploadScheduleButton />
                                    <div> <GenerateScheduleBurtton /></div>
                                </div>
                            )}
                        </div>
                    )}
                    <div className='planning__container__body flex-row'>
                        <LeftSidebar />
                        <RightSection
                            message="No Schedule to Preview"
                            icon={<NoSchedulePreview />}
                            currentTime={current_simulation_time / 1000}
                            selectedSchedule={onboardScheduleDetails.selectedSchedule}
                            selectedTask={onboardScheduleDetails.selectedTask}
                            selectedTelecommand={onboardScheduleDetails.selectedTelecommand}
                            name={onboardScheduleDetails.selectedSchedule?.scheduleName || ''}
                            startTime={onboardScheduleDetails.selectedSchedule?.startTimeISO8601 || null}
                        duration={onboardScheduleDetails.selectedSchedule?.duration || null}
                        subMessage="Schedule will be visible once a schedule is generated"
                        onMenuClick={onMenuClick}
                        onTaskSelect={onTaskSelect}
                        onCommandSelect={onCommandSelect}
                    />
                </div>
                </div>
            }

        </Fragment>
    )
}

export default OnboardSchedule
